import type {
  AdminBooking,
  AdminBookingAvailability,
  AdminBookingAvailabilityResponse,
  AdminBookingListResponse,
  AdminBookingResponse,
  BookingBalanceFilter,
  BookingListStatus,
  BookingLifecycleStatus,
  CancelAdminBookingRequest,
  ChangeAdminBookingPackageRequest,
  ConfirmAdminBookingRequest,
  CreateAdminBookingRequest,
  RecordAdminBookingPaymentRequest,
  UpdateAdminBookingRequest,
} from '@mds/shared/types/booking';

import { api } from '@/lib/api';

/** The booking and availability endpoints — see `backend/docs/bookings.md`. */

/** A type alias, so it carries the index signature the client's `query` needs. */
export type BookingQuery = {
  page?: number;
  limit?: number;
  /** yyyy-mm-dd, the day the booking is held for. */
  date?: string;
  resourceId?: string;
  customerId?: string;
  /** Both values leave cancelled bookings out — see `backend/docs/bookings.md`. */
  balance?: BookingBalanceFilter;
  status?: BookingLifecycleStatus;
  bookingStatus?: BookingListStatus;
  /** Used by Booking Requests; the main booking list always excludes cancelled rows. */
  excludeCancelled?: boolean;
  /** Matched across the `SB-` reference and customer name. */
  search?: string;
  sort?: 'bookingDate' | 'createdAt' | 'reference' | 'customerName' | 'balanceAmountMinor';
  direction?: 'asc' | 'desc';
};

export const listBookings = (query: BookingQuery = {}) =>
  api.get<AdminBookingListResponse>('admin/bookings', { query });

export const listBookingRequests = (query: BookingQuery = {}) =>
  api.get<AdminBookingListResponse>('admin/bookings/requests', { query });

export const getBooking = (bookingId: string): Promise<AdminBooking> =>
  api.get<AdminBookingResponse>(`admin/bookings/${bookingId}`).then((r) => r.data);

/**
 * The booking behind an `SB-###` reference. The Payments modal resolves one
 * before any money exists against it, which is why this is not a ledger search.
 */
export const getBookingByReference = (reference: string): Promise<AdminBooking> =>
  api
    .get<AdminBookingResponse>(`admin/bookings/by-reference/${encodeURIComponent(reference)}`)
    .then((r) => r.data);

/**
 * Desks on a floor, or the whole-hour slots in a room, for one date.
 *
 * Read fresh before every booking attempt: the server rechecks occupancy under
 * a lock when it writes, so a stale board loses the race rather than winning it.
 */
export const getAvailability = (
  resourceId: string,
  date: string,
  deskPackageItemId?: string,
  startMinute?: number,
  endMinute?: number,
): Promise<AdminBookingAvailability> =>
  api
    .get<AdminBookingAvailabilityResponse>('admin/bookings/availability', {
      query: { resourceId, date, deskPackageItemId, startMinute, endMinute },
    })
    .then((r) => r.data);

/**
 * Creates the booking. The request carries no price — the server snapshots the
 * active rate card — and `initialPayment` must be either the full total or
 * exactly the 50% deposit.
 */
export const createBooking = (body: CreateAdminBookingRequest): Promise<AdminBooking> =>
  api.post<AdminBookingResponse>('admin/bookings', body).then((r) => r.data);

/** Releases the allocation. Payment history and the retained deposit survive. */
export const cancelBooking = (
  bookingId: string,
  body: CancelAdminBookingRequest = {},
): Promise<AdminBooking> =>
  api.post<AdminBookingResponse>(`admin/bookings/${bookingId}/cancel`, body).then((r) => r.data);

export const confirmBookingRequest = (
  bookingId: string,
  body: ConfirmAdminBookingRequest = {},
): Promise<AdminBooking> =>
  api
    .post<AdminBookingResponse>(`admin/bookings/requests/${bookingId}/confirm`, body)
    .then((r) => r.data);

export const updateBookingRequest = (
  bookingId: string,
  body: UpdateAdminBookingRequest,
): Promise<AdminBooking> =>
  api.patch<AdminBookingResponse>(`admin/bookings/requests/${bookingId}`, body).then((r) => r.data);

/**
 * Swaps the desk package on a booking nothing has been paid against yet. The
 * server re-prices it and refuses once any payment exists.
 */
export const changeBookingPackage = (
  bookingId: string,
  body: ChangeAdminBookingPackageRequest,
): Promise<AdminBooking> =>
  api
    .patch<AdminBookingResponse>(`admin/bookings/${bookingId}/package`, body)
    .then((r) => r.data);

/** Takes money against an existing booking, from its detail view. */
export const recordBookingPayment = (
  bookingId: string,
  body: RecordAdminBookingPaymentRequest,
): Promise<AdminBooking> =>
  api.post<AdminBookingResponse>(`admin/bookings/${bookingId}/payments`, body).then((r) => r.data);
